<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <span class="panel-title">
          <i class="fa fa-desktop"></i>
          Workstation Version Management
        </span>
        <span class="version-badge" v-if="latest_version">
          <i class="fa fa-tag"></i>
          Latest: {{ latest_version }}
        </span>
      </div>

      <!-- Toolbar -->
      <div class="toolbar">

        <!-- Admin: Service Provider type-and-select -->
        <div class="field-group" v-if="currentUser.u.is_admin">
          <label>Service Provider</label>
          <div class="ts-wrap" ref="spWrap" v-click-outside="closeSpDropdown">
            <div class="ts-input" @click="toggleSp">
              <span class="ts-val" v-if="!spOpen">{{ spLabel }}</span>
              <input v-if="spOpen" type="text" class="ts-search" v-model="spSearch"
                placeholder="Search provider…" ref="spInput" @keydown.esc="closeSpDropdown" />
              <i class="fa fa-chevron-down ts-arrow" :class="{ open: spOpen }"></i>
            </div>
            <div class="ts-dropdown" v-if="spOpen" :style="spDropStyle">
              <div class="ts-item" @click="selectSp('all')">All Service Providers</div>
              <div class="ts-item" v-for="(c, i) in filteredSp" :key="i" @click="selectSp(c.id)">{{ c.name }}</div>
              <div class="ts-empty" v-if="filteredSp.length === 0 && spSearch">No match</div>
            </div>
          </div>
        </div>

        <!-- Admin: Location type-and-select -->
        <div class="field-group" v-if="currentUser.u.is_admin">
          <label>Location</label>
          <div class="ts-wrap" ref="locWrap" v-click-outside="closeLocDropdown">
            <div class="ts-input" @click="toggleLoc">
              <span class="ts-val" v-if="!locOpen">{{ locLabel }}</span>
              <input v-if="locOpen" type="text" class="ts-search" v-model="locSearch"
                placeholder="Search location…" ref="locInput" @keydown.esc="closeLocDropdown" />
              <i class="fa fa-chevron-down ts-arrow" :class="{ open: locOpen }"></i>
            </div>
            <div class="ts-dropdown" v-if="locOpen" :style="locDropStyle">
              <div class="ts-item" @click="selectLoc('all')">All Locations</div>
              <div class="ts-item" v-for="(c, i) in filteredLoc" :key="i" @click="selectLoc(c.id)">{{ c.name }}</div>
              <div class="ts-empty" v-if="filteredLoc.length === 0 && locSearch">No match</div>
            </div>
          </div>
        </div>

        <!-- SP user: Location type-and-select -->
        <div class="field-group" v-if="!currentUser.u.is_client && !currentUser.u.is_admin">
          <label>Location</label>
          <div class="ts-wrap" ref="locWrap" v-click-outside="closeLocDropdown">
            <div class="ts-input" @click="toggleLoc">
              <span class="ts-val" v-if="!locOpen">{{ locLabel }}</span>
              <input v-if="locOpen" type="text" class="ts-search" v-model="locSearch"
                placeholder="Search location…" ref="locInput" @keydown.esc="closeLocDropdown" />
              <i class="fa fa-chevron-down ts-arrow" :class="{ open: locOpen }"></i>
            </div>
            <div class="ts-dropdown" v-if="locOpen" :style="locDropStyle">
              <div class="ts-item" @click="selectLoc('all')">All Locations</div>
              <div class="ts-item" v-for="(c, i) in filteredLocSp" :key="i" @click="selectLoc(c.id)">{{ c.name }}</div>
              <div class="ts-empty" v-if="filteredLocSp.length === 0 && locSearch">No match</div>
            </div>
          </div>
        </div>

        <!-- Label filter -->
        <div class="field-group">
          <label>Label</label>
          <div class="label-input-wrap">
            <i class="fa fa-tag"></i>
            <input
              type="text"
              class="label-input"
              v-model="labelFilter"
              placeholder="Filter by label…"
            />
            <button v-if="labelFilter" class="label-clear" @click="labelFilter = ''; currentPage = 1">
              <i class="fa fa-times"></i>
            </button>
          </div>
        </div>

        <div class="toolbar-actions">
          <button class="btn-search" @click="loadApiData()">
            <i class="fa fa-search"></i> Search
          </button>
          <button class="btn-clear" @click="clearFilters()">
            <i class="fa fa-times"></i> Clear
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div class="state-box" v-if="loadingData">
        <i class="fa fa-spinner fa-spin"></i>
        Loading workstation data…
      </div>

      <!-- Empty -->
      <div class="state-box" v-else-if="!loadingData && filteredList.length === 0">
        <i class="fa fa-desktop"></i>
        No workstations found
      </div>

      <!-- Table -->
      <div v-else style="overflow-x: auto;">
        <table class="ver-table">
          <thead>
            <tr>
              <th style="width:44px;">
                <input class="custom-checkbox" type="checkbox" v-model="selectAll" @change="selectAllApiKeys()" />
              </th>
              <th style="width:48px;">#</th>
              <th>Location</th>
              <th>Label</th>
              <th style="text-align:center;">Current Version</th>
              <th style="text-align:center;">Update Status</th>
              <th style="width:200px;">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in pagedList" :key="index" :class="item.checked ? 'row-selected' : ''">
              <td>
                <input class="custom-checkbox" type="checkbox" v-model="item.checked"
                  @change="itemSelect(item, $event, item.id)" />
              </td>
              <td class="col-sn">{{ (currentPage - 1) * perPage + index + 1 }}</td>
              <td style="font-weight:500; color:#0d1b3e;">{{ item.client_name }}</td>
              <td style="color:#4a5568;">{{ item.label }}</td>
              <td style="text-align:center;">
                <span class="col-version"
                  :class="item.current_version && latest_version
                    ? (item.current_version === latest_version ? 'cv-latest' : 'cv-outdated')
                    : 'cv-unknown'">
                  {{ item.current_version || '—' }}
                </span>
              </td>
              <td style="text-align:center;">
                <span v-if="item.current_version === latest_version && latest_version !== ''"
                  class="status-badge s-current">
                  <i class="fa fa-check-circle"></i> Up to date
                </span>
                <span v-else-if="item.scheduled_for_update" class="status-badge s-scheduled">
                  <i class="fa fa-clock-o"></i> Scheduled
                </span>
                <span v-else class="status-badge s-pending">
                  <i class="fa fa-times-circle"></i> Not Scheduled
                </span>
              </td>
              <td>
                <div style="display:flex; gap:6px;">
                  <button class="btn-schedule schedule" @click="scheduleapikey(item)">
                    <i class="fa fa-calendar-check-o"></i> Schedule
                  </button>
                  <button class="btn-schedule unschedule" @click="unscheduleapikey(item)">
                    <i class="fa fa-ban"></i> Unschedule
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-bar" v-if="filteredList.length > perPage">
        <span class="pg-info">
          Showing {{ (currentPage - 1) * perPage + 1 }}–{{ Math.min(currentPage * perPage, filteredList.length) }}
          of {{ filteredList.length }}
        </span>
        <div class="pg-controls">
          <button class="pg-btn" :disabled="currentPage === 1" @click="currentPage = 1">
            <i class="fa fa-angle-double-left"></i>
          </button>
          <button class="pg-btn" :disabled="currentPage === 1" @click="currentPage--">
            <i class="fa fa-angle-left"></i>
          </button>
          <button v-for="p in visiblePages" :key="p" class="pg-btn" :class="{ active: p === currentPage }"
            @click="currentPage = p">{{ p }}</button>
          <button class="pg-btn" :disabled="currentPage === totalPages" @click="currentPage++">
            <i class="fa fa-angle-right"></i>
          </button>
          <button class="pg-btn" :disabled="currentPage === totalPages" @click="currentPage = totalPages">
            <i class="fa fa-angle-double-right"></i>
          </button>
        </div>
        <div class="pg-size">
          <select v-model="perPage" @change="currentPage = 1">
            <option :value="10">10 / page</option>
            <option :value="25">25 / page</option>
            <option :value="50">50 / page</option>
          </select>
        </div>
      </div>

    </div>

    <!-- ── Schedule Modal ── -->
    <div class="modal-overlay" v-if="activeModal === 'schedule'" @click.self="activeModal = null">
      <div class="confirm-dialog">
        <div class="modal-icon-wrap">
          <div class="modal-icon icon-blue"><i class="fa fa-calendar-check"></i></div>
        </div>
        <div class="modal-body-inner">
          <h3>Schedule Update</h3>
          <p>This workstation will be queued for a version update.</p>
          <div class="modal-item-card" v-if="modalItem">
            <div class="mic-icon"><i class="fa fa-desktop"></i></div>
            <div class="mic-info">
              <div class="mic-label">{{ modalItem.label }}</div>
              <div class="mic-sub">{{ modalItem.client_name }}</div>
            </div>
            <span class="mic-version" v-if="modalItem.current_version">{{ modalItem.current_version }}</span>
          </div>
        </div>
        <div class="confirm-footer">
          <button class="btn-cancel" @click="activeModal = null">Cancel</button>
          <button class="btn-confirm-blue" @click="confirmSchedule">
            <i class="fa fa-calendar-check"></i> Schedule
          </button>
        </div>
      </div>
    </div>

    <!-- ── Unschedule Modal ── -->
    <div class="modal-overlay" v-if="activeModal === 'unschedule'" @click.self="activeModal = null">
      <div class="confirm-dialog">
        <div class="modal-icon-wrap">
          <div class="modal-icon icon-danger"><i class="fa fa-ban"></i></div>
        </div>
        <div class="modal-body-inner">
          <h3>Unschedule Update</h3>
          <p>The scheduled update for this workstation will be removed.</p>
          <div class="modal-item-card" v-if="modalItem">
            <div class="mic-icon"><i class="fa fa-desktop"></i></div>
            <div class="mic-info">
              <div class="mic-label">{{ modalItem.label }}</div>
              <div class="mic-sub">{{ modalItem.client_name }}</div>
            </div>
          </div>
        </div>
        <div class="confirm-footer">
          <button class="btn-cancel" @click="activeModal = null">Cancel</button>
          <button class="btn-confirm-danger" @click="confirmUnschedule">
            <i class="fa fa-ban"></i> Unschedule
          </button>
        </div>
      </div>
    </div>

    <!-- ── Bulk Schedule Modal ── -->
    <div class="modal-overlay" v-if="activeModal === 'bulk'" @click.self="activeModal = null">
      <div class="confirm-dialog">
        <div class="modal-icon-wrap">
          <div class="modal-icon icon-orange"><i class="fa fa-calendar-alt"></i></div>
        </div>
        <div class="modal-body-inner">
          <h3>Schedule All Selected</h3>
          <p>The following workstations will be queued for a version update.</p>
          <div class="bulk-count-chip">
            <i class="fa fa-desktop"></i>
            {{ checked_list.length }} workstation{{ checked_list.length !== 1 ? 's' : '' }} selected
          </div>
        </div>
        <div class="confirm-footer">
          <button class="btn-cancel" @click="activeModal = null">Cancel</button>
          <button class="btn-confirm-orange" @click="confirmBulk">
            <i class="fa fa-calendar-alt"></i> Schedule All
          </button>
        </div>
      </div>
    </div>

    <!-- Bulk action bar -->
    <div class="bulk-bar" v-if="checked_list.length > 0">
      <div class="bulk-bar__inner">
        <span class="bulk-bar__info">
          <strong>{{ checked_list.length }}</strong> workstation{{ checked_list.length !== 1 ? 's' : '' }} selected
        </span>
        <button class="btn-bulk-schedule" @click="update()">
          <i class="fa fa-calendar-check-o"></i> Schedule All Selected
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  FETCH_APIKEY_VERSION,
  FETCH_CLIENTS,
  FETCH_SERVICES_CLIENTS,
  UPDATE_APIKEY_VERSION,
  UPDATE_APIKEY_VERSION_LISTS,
  UNSCHEDULE_APIKEY_VERSION
} from "@/core/services/store/actions.type";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";

export default {
  directives: {
    clickOutside: {
      bind(el, binding) {
        el._clickOutsideHandler = e => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener("mousedown", el._clickOutsideHandler);
      },
      unbind(el) {
        document.removeEventListener("mousedown", el._clickOutsideHandler);
      }
    }
  },
  computed: {
    ...mapGetters(["currentUser", "getApikeyVersionData", "getClients", "getSpClients"]),
    filteredSp() {
      const q = (this.spSearch || "").toLowerCase();
      const list = this.getClients || [];
      return q ? list.filter(c => c.name && c.name.toLowerCase().includes(q)) : list;
    },
    filteredLoc() {
      const q = (this.locSearch || "").toLowerCase();
      const list = this.getSpClients || [];
      return q ? list.filter(c => c.name && c.name.toLowerCase().includes(q)) : list;
    },
    filteredLocSp() {
      const q = (this.locSearch || "").toLowerCase();
      const list = this.getClients || [];
      return q ? list.filter(c => c.name && c.name.toLowerCase().includes(q)) : list;
    },
    spLabel() {
      if (this.providerOutlet === "all") return "All Service Providers";
      const c = (this.getClients || []).find(c => c.id === this.providerOutlet);
      return c ? c.name : "All Service Providers";
    },
    locLabel() {
      if (this.locationOutlet === "all") return "All Locations";
      const list = this.currentUser.u.is_admin ? (this.getSpClients || []) : (this.getClients || []);
      const c = list.find(c => c.id === this.locationOutlet);
      return c ? c.name : "All Locations";
    },
    filteredList() {
      const q = (this.labelFilter || "").trim().toLowerCase();
      if (!q) return this.apikeyversionlist;
      return this.apikeyversionlist.filter(item => item.label && item.label.toLowerCase().includes(q));
    },
    totalPages() {
      return Math.ceil(this.filteredList.length / this.perPage) || 1;
    },
    pagedList() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredList.slice(start, start + this.perPage);
    },
    visiblePages() {
      const total = this.totalPages;
      const cur = this.currentPage;
      const pages = [];
      for (let i = Math.max(1, cur - 2); i <= Math.min(total, cur + 2); i++) pages.push(i);
      return pages;
    }
  },
  watch: {
    labelFilter() { this.currentPage = 1; }
  },
  mounted() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.loadClients();
    this.loadApiData();
  },
  methods: {
    toggleSp() {
      this.spOpen = !this.spOpen;
      if (this.spOpen) {
        this.spSearch = "";
        this.$nextTick(() => {
          if (this.$refs.spInput) this.$refs.spInput.focus();
          const wrap = this.$refs.spWrap;
          if (wrap) {
            const r = wrap.getBoundingClientRect();
            this.spDropStyle = { position: "fixed", top: (r.bottom + 4) + "px", left: r.left + "px", width: r.width + "px", zIndex: 9999 };
          }
        });
      }
    },
    closeSpDropdown() { this.spOpen = false; this.spSearch = ""; this.spDropStyle = {}; },
    selectSp(id) {
      this.providerOutlet = id;
      this.locationOutlet = "all";
      this.closeSpDropdown();
      if (id !== "all") {
        this.$store.dispatch(FETCH_SERVICES_CLIENTS, id);
      }
    },
    toggleLoc() {
      this.locOpen = !this.locOpen;
      if (this.locOpen) {
        this.locSearch = "";
        this.$nextTick(() => {
          if (this.$refs.locInput) this.$refs.locInput.focus();
          const wrap = this.$refs.locWrap;
          if (wrap) {
            const r = wrap.getBoundingClientRect();
            this.locDropStyle = { position: "fixed", top: (r.bottom + 4) + "px", left: r.left + "px", width: r.width + "px", zIndex: 9999 };
          }
        });
      }
    },
    closeLocDropdown() { this.locOpen = false; this.locSearch = ""; this.locDropStyle = {}; },
    selectLoc(id) {
      this.locationOutlet = id;
      this.closeLocDropdown();
    },
    clearFilters() {
      this.providerOutlet = "all";
      this.locationOutlet = "all";
      this.labelFilter = "";
      this.loadApiData();
    },
    loadApiData() {
      this.loadingData = true;
      let body = this.currentUser.u.is_admin
        ? { service_outlet: this.locationOutlet, service_provider_outlet: this.providerOutlet }
        : { service_outlet: this.locationOutlet };
      this.$store.dispatch(FETCH_APIKEY_VERSION, body).then(data => {
        this.loadingData = false;
        if (data.success == true) {
          this.latest_version = data.latest_version;
          this.apikeyversionlist = data.data;
          this.currentPage = 1;
        }
      }).catch(() => { this.loadingData = false; });
    },
    async loadClients() {
      await this.$store.dispatch(FETCH_CLIENTS);
    },
    selectAllApiKeys() {
      if (this.selectAll) {
        this.checked_list = [...this.apikeyversionlist];
        this.apikeyversionlist.forEach(item => { item.checked = true; });
      } else {
        this.checked_list = [];
        this.apikeyversionlist.forEach(item => { item.checked = false; });
      }
    },
    itemSelect(item, $e, id) {
      if ($e.target.checked) {
        this.checked_list.push(item);
      } else {
        const index = this.checked_list.findIndex(i => i.id === id);
        if (index > -1) this.checked_list.splice(index, 1);
      }
    },
    scheduleapikey(item) {
      this.modalItem = item;
      this.activeModal = "schedule";
    },
    confirmSchedule() {
      this.activeModal = null;
      this.$store.dispatch(UPDATE_APIKEY_VERSION, this.modalItem.id)
        .then(data => { this.$toastr.s(data.msg); this.loadApiData(); })
        .catch(err => { this.$toastr.e(err.msg); });
    },
    unscheduleapikey(item) {
      this.modalItem = item;
      this.activeModal = "unschedule";
    },
    confirmUnschedule() {
      this.activeModal = null;
      this.$store.dispatch(UNSCHEDULE_APIKEY_VERSION, this.modalItem.id)
        .then(data => { this.$toastr.s(data.msg); this.loadApiData(); })
        .catch(err => { this.$toastr.e(err.msg); });
    },
    update() {
      if (this.checked_list.length === 0) {
        this.$toastr.e("Please select at least one workstation");
        return;
      }
      this.activeModal = "bulk";
    },
    confirmBulk() {
      this.activeModal = null;
      const selected = this.apikeyversionlist.filter(item => item.checked);
      this.$store.dispatch(UPDATE_APIKEY_VERSION_LISTS, selected)
        .then(data => {
          this.$toastr.s(data.msg);
          this.selectAll = false;
          this.checked_list = [];
          this.loadApiData();
        })
        .catch(err => { this.errors = err; this.selectAll = false; });
    }
  },
  data() {
    return {
      loadingData: false,
      latest_version: "",
      apikeyversionlist: [],
      selectAll: false,
      providerOutlet: "all",
      locationOutlet: "all",
      spOpen: false,
      spSearch: "",
      spDropStyle: {},
      locOpen: false,
      locSearch: "",
      locDropStyle: {},
      labelFilter: "",
      checked_list: [],
      currentPage: 1,
      perPage: 10,
      errors: [],
      activeModal: null,
      modalItem: null,
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Workstation Versions", route: "" }
      ]
    };
  }
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #dde2ec;
$green: #276749;

.page-wrap {
  padding: 8px 0 80px;
  font-family: $font;
}

/* ── Panel ── */
.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.07);
  overflow: visible;
  margin-bottom: 80px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #eef2fb;
  border-bottom: 1px solid #dde4f5;
  border-radius: 12px 12px 0 0;

  .panel-title {
    font-size: 13px;
    font-weight: 700;
    color: $blue;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .version-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #e8f5e9;
    border: 1px solid #a5d6a7;
    color: #2e7d32;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 20px;

    i { font-size: 11px; }
  }
}

/* ── Toolbar ── */
.toolbar {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f3fa;
  flex-wrap: wrap;

  .field-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 240px;
    width: 240px;

    label {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #7a8499;
    }
  }

  .toolbar-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    align-self: flex-end;
  }

  .btn-search,
  .btn-clear {
    height: 38px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    font-family: $font;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
    flex-shrink: 0;
  }

  .btn-search {
    padding: 0 20px;
    background: #fff;
    color: $blue;
    border: 1.5px solid $blue;

    &:hover { background: $blue; color: #fff; }
  }

  .btn-clear {
    padding: 0 16px;
    background: #fff;
    color: #7a8499;
    border: 1.5px solid $border;

    &:hover { border-color: #c53030; color: #c53030; background: #fff5f5; }
  }
}

/* ── Type-and-select ── */
.ts-wrap {
  position: relative;
}

.ts-input {
  display: flex;
  align-items: center;
  height: 38px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 10px;
  background: #fff;
  cursor: pointer;
  gap: 6px;
  transition: border-color 0.2s;

  &:hover { border-color: $blue; }
  &:focus-within {
    border-color: $blue;
    box-shadow: 0 0 0 3px rgba(56, 154, 212, 0.1);
  }
}

.ts-val {
  flex: 1;
  font-size: 13px;
  color: $dark;
  font-family: $font;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
}

.ts-search {
  flex: 1;
  border: none;
  outline: none;
  font-size: 13px;
  color: $dark;
  font-family: $font;
  background: transparent;
  min-width: 0;

  &::placeholder { color: #b0bac9; }
}

.ts-arrow {
  font-size: 10px;
  color: #a0aab8;
  flex-shrink: 0;
  transition: transform 0.2s;
  pointer-events: none;

  &.open { transform: rotate(180deg); }
}

.ts-dropdown {
  position: fixed;
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(13, 27, 62, 0.12);
  z-index: 9999;
  max-height: 210px;
  overflow-y: auto;
}

.ts-item {
  padding: 8px 12px;
  font-size: 13px;
  color: $dark;
  font-family: $font;
  cursor: pointer;
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
  transition: background 0.12s;

  &:hover { background: #f0f7fd; color: $blue; }
}

.ts-empty {
  padding: 10px 12px;
  font-size: 12px;
  color: #a0aab8;
  text-align: center;
  font-family: $font;
}

/* ── Label filter input ── */
.label-input-wrap {
  display: flex;
  align-items: center;
  height: 38px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 10px;
  background: #fff;
  gap: 7px;
  transition: border-color 0.2s;

  > i { font-size: 11px; color: #a0aab8; flex-shrink: 0; }

  &:focus-within {
    border-color: $blue;
    box-shadow: 0 0 0 3px rgba(56, 154, 212, 0.1);
  }
}

.label-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 13px;
  color: $dark;
  font-family: $font;
  background: transparent;
  min-width: 0;
  height: 100%;

  &::placeholder { color: #b0bac9; }
}

.label-clear {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #e2e8f0;
  border: none;
  color: #718096;
  font-size: 9px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  transition: background 0.15s;

  &:hover { background: #c53030; color: #fff; }
}

/* ── Table ── */
.ver-table {
  width: 100%;
  font-size: 13px;
  border-collapse: collapse;

  thead tr th {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #7a8499;
    padding: 10px 16px;
    border-bottom: 2px solid #eef0f8;
    background: #fff;
    white-space: nowrap;
  }

  tbody tr {
    border-bottom: 1px solid #f5f7fa;
    transition: background 0.15s;

    &:last-child { border-bottom: none; }
    &:hover { background: #f8fbff; }
    &.row-selected { background: #eff8ff; }

    td {
      padding: 11px 16px;
      color: #2d3748;
      vertical-align: middle;
    }
  }

  .col-sn {
    color: #a0aab8;
    font-size: 12px;
    width: 48px;
  }

  .col-version {
    font-family: monospace;
    font-size: 12px;
    padding: 3px 8px;
    border-radius: 4px;
    display: inline-block;
    font-weight: 600;

    &.cv-latest  { background: #dcfce7; color: #166534; }
    &.cv-outdated { background: #fee2e2; color: #991b1b; }
    &.cv-unknown  { background: #eef2fb; color: #185FA5; }
  }
}

/* ── Status badges ── */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;

  &.s-current   { background: #c6f6d5; color: #276749; }
  &.s-scheduled { background: #bee3f8; color: #2b6cb0; }
  &.s-pending   { background: #fed7d7; color: #c53030; }
}

/* ── Row action buttons ── */
.btn-schedule {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;

  &.schedule {
    background: #eef2fb;
    color: #185FA5;
    border-color: #c5d4f0;

    &:hover { background: $blue; color: #fff; border-color: $blue; }
  }

  &.unschedule {
    background: #fff5f5;
    color: #c62828;
    border-color: #fecaca;

    &:hover { background: #c62828; color: #fff; border-color: #c62828; }
  }
}

/* ── Checkbox ── */
.custom-checkbox {
  width: 16px;
  height: 16px;
  accent-color: $blue;
  cursor: pointer;
}

/* ── Empty / loading states ── */
.state-box {
  text-align: center;
  padding: 48px 24px;
  color: #a0aab8;
  font-size: 13px;

  i { font-size: 28px; display: block; margin-bottom: 10px; opacity: 0.5; }
}

/* ── Pagination ── */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid #f0f3fa;
  flex-wrap: wrap;
  gap: 10px;

  .pg-info { font-size: 12px; color: #7a8499; white-space: nowrap; }
  .pg-controls { display: flex; align-items: center; gap: 4px; }

  .pg-btn {
    min-width: 32px;
    height: 32px;
    padding: 0 8px;
    border: 1.5px solid $border;
    background: #fff;
    color: #4a5568;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    font-family: $font;
    cursor: pointer;
    transition: all 0.15s;

    &:hover:not(:disabled) { border-color: $blue; color: $blue; }
    &:disabled { opacity: 0.35; cursor: default; }
    &.active { background: $blue; border-color: $blue; color: #fff; }
  }

  .pg-size select {
    height: 32px;
    border: 1.5px solid $border;
    border-radius: 6px;
    padding: 0 8px;
    font-size: 12px;
    color: #4a5568;
    background: #fff;
    cursor: pointer;
    outline: none;

    &:focus { border-color: $blue; }
  }
}

/* ── Custom modals ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(13, 27, 62, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(3px);
  padding: 20px;
}

.confirm-dialog {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(13, 27, 62, 0.22);
  width: 100%;
  max-width: 420px;
  overflow: hidden;
}

.modal-icon-wrap {
  display: flex;
  justify-content: center;
  padding: 28px 24px 0;
}

.modal-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;

  &.icon-blue   { background: #dbeafe; color: #1d4ed8; }
  &.icon-danger { background: #fed7d7; color: #c62828; }
  &.icon-orange { background: #feebc8; color: #c05621; }
}

.modal-body-inner {
  padding: 18px 28px 8px;
  text-align: center;

  h3 {
    font-size: 16px;
    font-weight: 700;
    color: $dark;
    margin: 10px 0 8px;
    font-family: $font;
  }

  p {
    font-size: 13px;
    color: #7a8499;
    margin: 0 0 14px;
    line-height: 1.6;
    font-family: $font;
  }
}

.modal-item-card {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f8fafd;
  border: 1px solid #e8edf5;
  border-radius: 10px;
  padding: 11px 14px;
  text-align: left;

  .mic-icon {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: #eef2fb;
    color: #185FA5;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
  }

  .mic-info { flex: 1; min-width: 0; }

  .mic-label {
    font-size: 13px;
    font-weight: 700;
    color: $dark;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: $font;
  }

  .mic-sub {
    font-size: 11.5px;
    color: #7a8499;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: $font;
  }

  .mic-version {
    font-family: monospace;
    font-size: 11px;
    font-weight: 700;
    background: #eef0f8;
    color: #4a5568;
    padding: 2px 7px;
    border-radius: 4px;
    flex-shrink: 0;
  }
}

.bulk-count-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #c05621;
  font-size: 13px;
  font-weight: 700;
  padding: 8px 16px;
  border-radius: 20px;
  font-family: $font;

  i { font-size: 13px; }
}

.confirm-footer {
  display: flex;
  gap: 10px;
  padding: 18px 28px 24px;

  button {
    flex: 1;
    height: 40px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: 1.5px solid;
    font-family: $font;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
}

.btn-cancel {
  background: #fff;
  color: #4a5568;
  border-color: $border;
  &:hover { border-color: #a0aab8; color: $dark; }
}

.btn-confirm-blue {
  background: #1d4ed8;
  color: #fff;
  border-color: #1d4ed8;
  &:hover { background: #1e40af; border-color: #1e40af; }
}

.btn-confirm-danger {
  background: #c62828;
  color: #fff;
  border-color: #c62828;
  &:hover { background: #9b1c1c; border-color: #9b1c1c; }
}

.btn-confirm-orange {
  background: #c05621;
  color: #fff;
  border-color: #c05621;
  &:hover { background: #9c4318; border-color: #9c4318; }
}

/* ── Bulk action footer ── */
.bulk-bar {
  position: fixed;
  bottom: 2.5rem;
  width: calc(78vw);
  z-index: 100;

  .bulk-bar__inner {
    background: $dark;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(13, 27, 62, 0.3);
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .bulk-bar__info {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.8);

      strong { color: #fff; }
    }

    .btn-bulk-schedule {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 22px;
      background: #F58020;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      font-family: $font;
      cursor: pointer;
      transition: opacity 0.2s;

      &:hover { opacity: 0.9; }
    }
  }
}
</style>
